import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { ArrowRight, Sparkles, Gift, Zap } from 'lucide-react';

export function OnboardingScreen() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const isTr = true; // Default to Turkish for onboarding

  if (!user || user.onboarding_completed !== false) return null;

  const handleStart = async () => {
    try {
      await api.post('/api/auth/onboarding-complete');
    } catch {}
    navigate('/studio');
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#07090C] flex items-center justify-center p-6" data-testid="onboarding-screen">
      {/* Background effects */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-gradient-to-b from-[#D4AF37]/[0.08] via-[#00B8A9]/[0.04] to-transparent blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-md w-full text-center">
        {/* Logo */}
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#00B8A9] flex items-center justify-center mx-auto mb-8 shadow-[0_8px_32px_-8px_rgba(212,175,55,0.4)]">
          <span className="text-2xl font-bold text-black" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>T</span>
        </div>

        {/* Welcome text */}
        <p className="text-sm text-[#D4AF37] font-medium mb-4 flex items-center justify-center gap-1.5">
          <Gift size={14} />
          {isTr ? `Hoş geldin, ${user.name?.split(' ')[0] || ''}!` : `Welcome, ${user.name?.split(' ')[0] || ''}!`}
        </p>

        {/* Main heading */}
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4 leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          {isTr ? (
            <>İlk içeriğini<br /><span className="text-[#D4AF37]">ücretsiz</span> üret</>
          ) : (
            <>Create your first<br />content <span className="text-[#D4AF37]">for free</span></>
          )}
        </h1>

        {/* Subtitle */}
        <p className="text-[15px] text-[#8B95A5] leading-relaxed mb-3 max-w-sm mx-auto">
          {isTr
            ? 'İlk 3 içeriğin için kredi düşmez. TekCep\'i gerçekten dene.'
            : 'Your first 3 posts are free. Try TekCep for real.'}
        </p>

        <p className="text-[12px] text-[#4B5563] mb-10">
          {isTr
            ? 'AI içerik üretimi · Görsel oluşturma · Kanal paylaşımı'
            : 'AI content generation · Image creation · Channel publishing'}
        </p>

        {/* CTA */}
        <Button
          onClick={handleStart}
          className="w-full max-w-xs mx-auto h-14 rounded-2xl bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#0A0C10] font-semibold text-base shadow-[0_8px_32px_-8px_rgba(212,175,55,0.4)] hover:shadow-[0_12px_40px_-8px_rgba(212,175,55,0.5)] transition-shadow"
          data-testid="onboarding-cta"
        >
          {isTr ? 'İçeriğimi Oluştur' : 'Create My Content'}
          <ArrowRight size={18} className="ml-2" />
        </Button>

        {/* Trust notes */}
        <div className="flex items-center justify-center gap-4 mt-8 text-[11px] text-[#4B5563]">
          <span className="flex items-center gap-1"><Zap size={10} className="text-[#D4AF37]" /> {isTr ? 'Kredi düşmez' : 'No credits charged'}</span>
          <span className="w-px h-3 bg-white/10" />
          <span className="flex items-center gap-1"><Sparkles size={10} className="text-[#00B8A9]" /> {isTr ? 'AI aktif' : 'AI enabled'}</span>
        </div>
      </div>
    </div>
  );
}

export function FreeTrialBadge({ remaining }) {
  if (!remaining || remaining <= 0) return null;
  return (
    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#D4AF37]/[0.08] border border-[#D4AF37]/15" data-testid="free-trial-badge">
      <Gift size={12} className="text-[#D4AF37]" />
      <span className="text-[11px] font-medium text-[#D4AF37]">Free Trial</span>
      <span className="text-[10px] text-[#D4AF37]/60">({remaining} left)</span>
    </div>
  );
}

export function TrialEndBanner({ onViewPlans }) {
  return (
    <div className="rounded-2xl border border-[#D4AF37]/15 bg-gradient-to-r from-[#D4AF37]/[0.04] to-[#00B8A9]/[0.04] p-5 text-center" data-testid="trial-end-banner">
      <p className="text-sm font-semibold mb-1">
        <Gift size={14} className="inline mr-1.5 text-[#D4AF37]" />
        Tebrikler, TekCep'i denedin!
      </p>
      <p className="text-[12px] text-[#8B95A5] mb-4">
        Artık içeriklerini kesintisiz paylaşmak için planını seç
      </p>
      <Button onClick={onViewPlans}
        className="bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#0A0C10] font-semibold text-sm rounded-xl h-10 px-6"
        data-testid="trial-end-cta">
        Planları Gör
        <ArrowRight size={14} className="ml-1.5" />
      </Button>
    </div>
  );
}
